import React, { useState, useEffect, useCallback } from 'react';
import { 
  Server, 
  Github, 
  GitBranch,
  CheckCircle, 
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Zap,
  Copy,
  Check,
  ExternalLink,
  Radio,
  Bell,
  Key
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import AdoStatusMappingModal from './AdoStatusMappingModal';
import { productIntegrationsApi, orgIntegrationsApi } from '../../services/api';
import { useFeatureFlags, FEATURE_IDS } from '../../hooks/useFeatureFlags';

const ProductIntegrationsSection = ({ productId, colors, canEdit }) => {
  const [integrations, setIntegrations] = useState(null);
  const [orgIntegrations, setOrgIntegrations] = useState(null);
  const [availableRepos, setAvailableRepos] = useState([]);
  const [availableTicketing, setAvailableTicketing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [expandedSection, setExpandedSection] = useState('mcp');
  const [copied, setCopied] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const { isEnabled } = useFeatureFlags();
  const hasGithub = isEnabled(FEATURE_IDS.INTEGRATION_GITHUB);
  const hasJira = isEnabled(FEATURE_IDS.INTEGRATION_JIRA);
  const hasLinear = isEnabled(FEATURE_IDS.INTEGRATION_LINEAR);
  // ADO doesn't need a feature flag — it's available if the user has connected their account
  const hasAdoAvailable = availableTicketing.some(o => o.provider === 'azure_devops' && o.available);
  const hasTicketing = hasGithub || hasJira || hasLinear || hasAdoAvailable;

  // Load integrations
  const loadData = useCallback(async () => {
    if (!productId) return;
    
    try {
      setLoading(true);
      const [integrationsRes, orgRes, reposRes, ticketingRes] = await Promise.all([
        productIntegrationsApi.getIntegrations(productId),
        orgIntegrationsApi.getIntegrations().catch(() => ({ data: {} })),
        productIntegrationsApi.getAvailableRepos(productId).catch(() => ({ data: { repos: [] } })),
        productIntegrationsApi.getAvailableTicketing(productId).catch(() => ({ data: { options: [] } }))
      ]);
      
      setIntegrations(integrationsRes.data);
      setOrgIntegrations(orgRes.data);
      setAvailableRepos(reposRes.data.repos || []);
      setAvailableTicketing(ticketingRes.data.options || []);
    } catch (error) {
      console.error('Failed to load integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleMCPToggle = async (enabled) => {
    try {
      setSaving('mcp');
      await productIntegrationsApi.updateMCP(productId, { enabled });
      setIntegrations(prev => ({
        ...prev,
        mcp: { ...prev.mcp, enabled }
      }));
      toast.success(enabled ? 'MCP enabled' : 'MCP disabled');
    } catch (error) {
      console.error('Failed to update MCP:', error);
      toast.error('Failed to update MCP settings');
    } finally {
      setSaving(null);
    }
  };

  const handleSourceControlUpdate = async (repos) => {
    try {
      setSaving('source_control');
      await productIntegrationsApi.updateSourceControl(productId, {
        provider: repos.length > 0 ? 'github' : null,
        repos
      });
      setIntegrations(prev => ({
        ...prev,
        source_control: repos.length > 0 ? { provider: 'github', repos } : null
      }));
      toast.success('Source control updated');
    } catch (error) {
      console.error('Failed to update source control:', error);
      toast.error('Failed to update source control');
    } finally {
      setSaving(null);
    }
  };

  const handleTicketingUpdate = async (provider, config = {}) => {
    try {
      setSaving('ticketing');
      await productIntegrationsApi.updateTicketing(productId, {
        provider,
        ...config
      });
      setIntegrations(prev => ({
        ...prev,
        ticketing: provider ? { provider, ...config } : null
      }));
      toast.success('Ticketing updated');
    } catch (error) {
      console.error('Failed to update ticketing:', error);
      toast.error(error.response?.data?.detail || 'Failed to update ticketing');
    } finally {
      setSaving(null);
    }
  };

  const handleStatusMappingSave = async (mapping) => {
    try {
      setSaving('ticketing');
      const currentTicketing = integrations?.ticketing || {};
      await productIntegrationsApi.updateTicketing(productId, {
        ...currentTicketing,
        status_mapping: mapping,
      });
      setIntegrations(prev => ({
        ...prev,
        ticketing: { ...prev.ticketing, status_mapping: mapping }
      }));
      setShowMappingModal(false);
      toast.success('Status mapping saved');
    } catch (error) {
      console.error('Failed to save mapping:', error);
      toast.error('Failed to save status mapping');
    } finally {
      setSaving(null);
    }
  };

  const handleAutoStatusRuleToggle = async (trigger, enabled) => {
    try {
      setSaving('automation');
      const currentRules = integrations?.automation?.auto_status_rules || [];
      const updatedRules = currentRules.map(rule => 
        rule.trigger === trigger ? { ...rule, enabled } : rule
      );
      
      await productIntegrationsApi.updateAutomation(productId, {
        auto_status_rules: updatedRules
      });
      
      setIntegrations(prev => ({
        ...prev,
        automation: {
          ...prev.automation,
          auto_status_rules: updatedRules
        }
      }));
      toast.success('Automation rule updated');
    } catch (error) {
      console.error('Failed to update automation:', error);
      toast.error('Failed to update automation');
    } finally {
      setSaving(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleRepoSelection = (repo) => {
    const currentRepos = integrations?.source_control?.repos || [];
    const newRepos = currentRepos.includes(repo)
      ? currentRepos.filter(r => r !== repo)
      : [...currentRepos, repo];
    handleSourceControlUpdate(newRepos);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Loading integrations...</span>
      </div>
    );
  }

  const mcpEnabled = integrations?.mcp?.enabled !== false;
  const mcpServerUrl = integrations?.mcp?.server_url || `https://mcp.stackranked.ai/sse`;
  const githubConnected = !!orgIntegrations?.github;
  const selectedRepos = integrations?.source_control?.repos || [];
  const selectedTicketing = integrations?.ticketing?.provider || null;
  const selectedTicketingRepo = integrations?.ticketing?.github_repo || null;
  const autoStatusRules = (integrations?.automation?.auto_status_rules || [])
    .filter(rule => hasGithub || rule.trigger !== 'pr_merged');

  return (
    <>
    <div className="space-y-4">
      {/* MCP Server Section */}
      <Card className={cn("border overflow-hidden", colors.borderPrimary)}>
        <button
          onClick={() => toggleSection('mcp')}
          className={cn(
            "w-full px-4 py-3 flex items-center justify-between text-left",
            "hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
          )}
        >
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center",
              mcpEnabled 
                ? "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
                : "bg-gray-100 dark:bg-gray-800 text-gray-500"
            )}>
              <Server className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className={cn("font-medium", colors.textPrimary)}>MCP Server</span>
                <Badge 
                  variant="outline" 
                  className={mcpEnabled 
                    ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400" 
                    : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                  }
                >
                  {mcpEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <p className={cn("text-sm", colors.textMuted)}>
                AI assistants pull feature context via MCP protocol
              </p>
            </div>
          </div>
          {expandedSection === 'mcp' ? (
            <ChevronDown className={cn("w-5 h-5", colors.textMuted)} />
          ) : (
            <ChevronRight className={cn("w-5 h-5", colors.textMuted)} />
          )}
        </button>
        
        {expandedSection === 'mcp' && (
          <CardContent className={cn("border-t pt-4", colors.borderPrimary)}>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className={colors.textPrimary}>Enable MCP Server</Label>
                <Switch
                  checked={mcpEnabled}
                  onCheckedChange={handleMCPToggle}
                  disabled={saving === 'mcp' || !canEdit}
                />
              </div>
              
              {mcpEnabled && (
                <div className={cn("p-3 rounded-lg", colors.bgTertiary)}>
                  <Label className={cn("text-sm mb-2 block", colors.textMuted)}>Server Endpoint</Label>
                  <div className="flex items-center gap-2">
                    <code className={cn("flex-1 text-sm font-mono px-3 py-2 rounded break-all", colors.bgInput, colors.textPrimary)}>
                      {mcpServerUrl}?key=[your-api-key]
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(`${mcpServerUrl}?key=[your-api-key]`)}
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className={cn("mt-3 flex items-start gap-2 text-sm", colors.textMuted)}>
                    <Key className="w-4 h-4 mt-0.5 shrink-0" />
                    <p>
                      Replace <code className="font-mono text-xs px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700">[your-api-key]</code> with
                      your API key. You can create and manage keys in{' '}
                      <a
                        href="/settings?tab=api-keys"
                        className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                        data-testid="mcp-api-keys-link"
                      >
                        Settings &rarr; API Keys
                      </a>.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Source Control Section - requires INTEGRATION_GITHUB */}
      {hasGithub && (
      <Card className={cn("border overflow-hidden", colors.borderPrimary)}>
        <button
          onClick={() => toggleSection('source_control')}
          className={cn(
            "w-full px-4 py-3 flex items-center justify-between text-left",
            "hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
          )}
        >
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center",
              selectedRepos.length > 0
                ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                : "bg-gray-100 dark:bg-gray-800 text-gray-500"
            )}>
              <GitBranch className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className={cn("font-medium", colors.textPrimary)}>Source Control</span>
                {selectedRepos.length > 0 && (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400">
                    {selectedRepos.length} repo{selectedRepos.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              <p className={cn("text-sm", colors.textMuted)}>
                {githubConnected 
                  ? 'Select repositories for commit tracking'
                  : 'Connect GitHub at Organization Settings first'
                }
              </p>
            </div>
          </div>
          {expandedSection === 'source_control' ? (
            <ChevronDown className={cn("w-5 h-5", colors.textMuted)} />
          ) : (
            <ChevronRight className={cn("w-5 h-5", colors.textMuted)} />
          )}
        </button>
        
        {expandedSection === 'source_control' && (
          <CardContent className={cn("border-t pt-4", colors.borderPrimary)}>
            {githubConnected ? (
              <div className="space-y-3">
                <Label className={cn("text-sm", colors.textMuted)}>
                  Select repositories to track commits with [feat_xxx] tags
                </Label>
                <div className={cn("max-h-48 overflow-y-auto rounded-lg border p-2", colors.borderPrimary)}>
                  {availableRepos.length > 0 ? (
                    availableRepos.map(repo => (
                      <label
                        key={repo}
                        className={cn(
                          "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRepos.includes(repo)}
                          onChange={() => toggleRepoSelection(repo)}
                          disabled={saving === 'source_control' || !canEdit}
                          className="rounded text-indigo-600"
                        />
                        <Github className="w-4 h-4 text-gray-500" />
                        <span className={cn("text-sm", colors.textPrimary)}>{repo}</span>
                      </label>
                    ))
                  ) : (
                    <p className={cn("text-sm italic py-2 text-center", colors.textMuted)}>
                      No repositories available. Sync repos at Organization Settings.
                    </p>
                  )}
                </div>
                {selectedRepos.length > 0 && (
                  <p className={cn("text-xs", colors.textMuted)}>
                    Commits with [feat_xxx] in the message will auto-link to features.
                  </p>
                )}
              </div>
            ) : (
              <div className={cn("p-4 rounded-lg text-center", colors.bgTertiary)}>
                <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className={cn("text-sm", colors.textMuted)}>
                  GitHub not connected at organization level.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => window.location.href = '/settings?tab=integrations'}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Go to Organization Settings
                </Button>
              </div>
            )}
          </CardContent>
        )}
      </Card>
      )}

      {/* Ticketing Section - requires at least one ticketing integration flag */}
      {hasTicketing && (
      <Card className={cn("border overflow-hidden", colors.borderPrimary)}>
        <button
          onClick={() => toggleSection('ticketing')}
          className={cn(
            "w-full px-4 py-3 flex items-center justify-between text-left",
            "hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
          )}
        >
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center",
              selectedTicketing
                ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                : "bg-gray-100 dark:bg-gray-800 text-gray-500"
            )}>
              <Radio className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className={cn("font-medium", colors.textPrimary)}>Ticketing</span>
                {selectedTicketing && (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400">
                    {selectedTicketing === 'github_issues' ? 'GitHub Issues' : selectedTicketing === 'azure_devops' ? 'Azure DevOps' : selectedTicketing}
                  </Badge>
                )}
              </div>
              <p className={cn("text-sm", colors.textMuted)}>
                Sync features with external ticketing system
              </p>
            </div>
          </div>
          {expandedSection === 'ticketing' ? (
            <ChevronDown className={cn("w-5 h-5", colors.textMuted)} />
          ) : (
            <ChevronRight className={cn("w-5 h-5", colors.textMuted)} />
          )}
        </button>
        
        {expandedSection === 'ticketing' && (
          <CardContent className={cn("border-t pt-4", colors.borderPrimary)}>
            <div className="space-y-3">
              <Label className={cn("text-sm", colors.textMuted)}>
                Choose one ticketing system (optional)
              </Label>
              
              <div className="space-y-2">
                {/* None option */}
                <label className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                  !selectedTicketing ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : colors.borderPrimary
                )}>
                  <input
                    type="radio"
                    name="ticketing"
                    checked={!selectedTicketing}
                    onChange={() => handleTicketingUpdate(null)}
                    disabled={saving === 'ticketing' || !canEdit}
                    className="text-indigo-600"
                  />
                  <div>
                    <p className={cn("font-medium text-sm", colors.textPrimary)}>None</p>
                    <p className={cn("text-xs", colors.textMuted)}>Manual status management</p>
                  </div>
                </label>
                
                {/* Ticketing options */}
                {availableTicketing.map(option => (
                  <div key={option.provider} className="space-y-2">
                    <label
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                        selectedTicketing === option.provider 
                          ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" 
                          : colors.borderPrimary,
                        !option.available && "opacity-60 cursor-not-allowed"
                      )}
                    >
                      <input
                        type="radio"
                        name="ticketing"
                        checked={selectedTicketing === option.provider}
                        onChange={() => {
                          if (option.available) {
                            if (option.provider === 'github_issues') {
                              // Just select the provider, repo selection will trigger the save
                              setIntegrations(prev => ({
                                ...prev,
                                ticketing: { provider: option.provider, github_repo: prev?.ticketing?.github_repo || null }
                              }));
                            } else if (option.provider === 'azure_devops') {
                              // Just select the provider, project selection will trigger the save
                              setIntegrations(prev => ({
                                ...prev,
                                ticketing: { provider: option.provider, ado_organization: prev?.ticketing?.ado_organization || null, ado_project: prev?.ticketing?.ado_project || null, ado_work_item_type: prev?.ticketing?.ado_work_item_type || 'Feature' }
                              }));
                            } else {
                              handleTicketingUpdate(option.provider);
                            }
                          }
                        }}
                        disabled={!option.available || saving === 'ticketing' || !canEdit}
                        className="text-indigo-600"
                      />
                      <div className="flex-1">
                        <p className={cn("font-medium text-sm", colors.textPrimary)}>{option.name}</p>
                        <p className={cn("text-xs", colors.textMuted)}>
                          {option.available 
                            ? `Sync features ↔ ${option.name.toLowerCase()}`
                            : option.message
                          }
                        </p>
                      </div>
                      {!option.available && (
                        <Badge variant="secondary" className="text-xs">Not Connected</Badge>
                      )}
                    </label>
                    
                    {/* GitHub repo selector - shown when GitHub Issues is selected */}
                    {option.provider === 'github_issues' && selectedTicketing === 'github_issues' && option.available && (
                      <div className="ml-8 p-3 rounded-lg border border-dashed border-indigo-300 dark:border-indigo-700 bg-indigo-50/50 dark:bg-indigo-900/10">
                        <Label className={cn("text-xs font-medium mb-2 block", colors.textMuted)}>
                          Select repository for GitHub Issues
                        </Label>
                        {availableRepos.length > 0 ? (
                          <select
                            value={selectedTicketingRepo || ''}
                            onChange={(e) => {
                              const repo = e.target.value;
                              if (repo) {
                                handleTicketingUpdate('github_issues', { github_repo: repo });
                              }
                            }}
                            disabled={saving === 'ticketing' || !canEdit}
                            className={cn(
                              "w-full px-3 py-2 rounded-md border text-sm",
                              colors.borderPrimary,
                              colors.bgSecondary,
                              colors.textPrimary,
                              "focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            )}
                          >
                            <option value="">-- Select a repository --</option>
                            {availableRepos.map(repo => (
                              <option key={repo} value={repo}>{repo}</option>
                            ))}
                          </select>
                        ) : (
                          <p className={cn("text-xs italic", colors.textMuted)}>
                            No repositories available. Sync repos at Organization Settings.
                          </p>
                        )}
                        {selectedTicketingRepo && (
                          <p className={cn("text-xs mt-2 flex items-center gap-1", "text-green-600 dark:text-green-400")}>
                            <CheckCircle className="w-3 h-3" />
                            Issues will be created in {selectedTicketingRepo}
                          </p>
                        )}
                        {!selectedTicketingRepo && (
                          <p className={cn("text-xs mt-2 flex items-center gap-1", "text-amber-600 dark:text-amber-400")}>
                            <AlertCircle className="w-3 h-3" />
                            Select a repository to enable issue creation
                          </p>
                        )}
                      </div>
                    )}

                    {/* ADO project/type selector - shown when Azure DevOps is selected */}
                    {option.provider === 'azure_devops' && selectedTicketing === 'azure_devops' && option.available && (
                      <div className="ml-8 p-3 rounded-lg border border-dashed border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10 space-y-3" data-testid="ado-ticketing-config">
                        <Label className={cn("text-xs font-medium block", colors.textMuted)}>
                          Configure Azure DevOps Work Items
                        </Label>
                        {/* Project selector */}
                        <div>
                          <Label className={cn("text-xs block mb-1", colors.textMuted)}>Project</Label>
                          <select
                            value={integrations?.ticketing?.ado_project ? `${integrations.ticketing.ado_organization}/${integrations.ticketing.ado_project}` : ''}
                            onChange={(e) => {
                              const [org, project] = e.target.value.split('/');
                              if (org && project) {
                                handleTicketingUpdate('azure_devops', {
                                  ado_organization: org,
                                  ado_project: project,
                                  ado_work_item_type: integrations?.ticketing?.ado_work_item_type || 'Feature',
                                });
                              }
                            }}
                            disabled={saving === 'ticketing' || !canEdit}
                            className={cn("w-full px-3 py-2 rounded-md border text-sm", colors.borderPrimary, colors.bgSecondary, colors.textPrimary, "focus:outline-none focus:ring-2 focus:ring-blue-500")}
                            data-testid="ado-project-select"
                          >
                            <option value="">-- Select a project --</option>
                            {(option.projects || []).map(proj => (
                              <option key={`${proj.org}/${proj.name}`} value={`${proj.org}/${proj.name}`}>
                                {proj.org} / {proj.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {/* Work Item Type */}
                        {integrations?.ticketing?.ado_project && (
                          <div>
                            <Label className={cn("text-xs block mb-1", colors.textMuted)}>Work Item Type</Label>
                            <select
                              value={integrations?.ticketing?.ado_work_item_type || 'Feature'}
                              onChange={(e) => {
                                handleTicketingUpdate('azure_devops', {
                                  ado_organization: integrations.ticketing.ado_organization,
                                  ado_project: integrations.ticketing.ado_project,
                                  ado_work_item_type: e.target.value,
                                });
                              }}
                              disabled={saving === 'ticketing' || !canEdit}
                              className={cn("w-full px-3 py-2 rounded-md border text-sm", colors.borderPrimary, colors.bgSecondary, colors.textPrimary, "focus:outline-none focus:ring-2 focus:ring-blue-500")}
                              data-testid="ado-wi-type-select"
                            >
                              <option value="Feature">Feature</option>
                              <option value="User Story">User Story</option>
                              <option value="Product Backlog Item">Product Backlog Item</option>
                              <option value="Bug">Bug</option>
                              <option value="Task">Task</option>
                            </select>
                          </div>
                        )}
                        {integrations?.ticketing?.ado_project && (
                          <p className={cn("text-xs flex items-center gap-1", "text-green-600 dark:text-green-400")}>
                            <CheckCircle className="w-3 h-3" />
                            Work items will be created in {integrations.ticketing.ado_organization}/{integrations.ticketing.ado_project}
                          </p>
                        )}
                        {/* Status Mapping Button */}
                        {integrations?.ticketing?.ado_project && (
                          <div className="pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowMappingModal(true)}
                              className={cn("text-xs w-full", colors.borderPrimary)}
                              data-testid="ado-configure-mapping-btn"
                            >
                              {integrations.ticketing.status_mapping ? 'Edit Status Mapping' : 'Configure Status Mapping'}
                            </Button>
                            {integrations.ticketing.status_mapping && (
                              <p className={cn("text-xs mt-1.5 flex items-center gap-1", colors.textMuted)}>
                                <CheckCircle className="w-3 h-3 text-green-500" />
                                {Object.keys(integrations.ticketing.status_mapping).length} states mapped
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ADO not connected prompt */}
                    {option.provider === 'azure_devops' && !option.available && option.user_connection_required && (
                      <div className="ml-8 p-3 rounded-lg border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10">
                        <p className={cn("text-xs flex items-center gap-2", colors.textMuted)}>
                          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          <span>
                            Azure DevOps requires a personal connection.{' '}
                            <a href="/settings?tab=profile" className="text-blue-500 hover:underline font-medium">
                              Connect in Profile Settings
                            </a>
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        )}
      </Card>
      )}

      {/* Automation / PR Done Section - requires INTEGRATION_GITHUB */}
      {hasGithub && (
      <Card className={cn("border overflow-hidden", colors.borderPrimary)}>
        <button
          onClick={() => toggleSection('automation')}
          className={cn(
            "w-full px-4 py-3 flex items-center justify-between text-left",
            "hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
          )}
        >
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center",
              "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"
            )}>
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <span className={cn("font-medium", colors.textPrimary)}>Automation</span>
              <p className={cn("text-sm", colors.textMuted)}>
                Auto-status rules and notifications
              </p>
            </div>
          </div>
          {expandedSection === 'automation' ? (
            <ChevronDown className={cn("w-5 h-5", colors.textMuted)} />
          ) : (
            <ChevronRight className={cn("w-5 h-5", colors.textMuted)} />
          )}
        </button>
        
        {expandedSection === 'automation' && (
          <CardContent className={cn("border-t pt-4", colors.borderPrimary)}>
            <div className="space-y-4">
              <div>
                <Label className={cn("text-sm font-medium mb-3 block", colors.textPrimary)}>
                  Auto-Status Rules
                </Label>
                <div className="space-y-2">
                  {autoStatusRules.map(rule => (
                    <div 
                      key={rule.trigger}
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg",
                        rule.enabled ? "bg-green-50 dark:bg-green-900/20" : colors.bgTertiary
                      )}
                    >
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline" className={rule.enabled ? "bg-green-100 border-green-300 text-green-700" : ""}>
                          {rule.trigger === 'first_mcp_activity' ? 'MCP' : 
                           rule.trigger === 'all_criteria_done' ? 'Criteria' :
                           rule.trigger === 'pr_merged' ? 'PR' : rule.trigger}
                        </Badge>
                        <span className={colors.textMuted}>→</span>
                        <span className={colors.textPrimary}>
                          {rule.action === 'status_in_progress' ? 'In Progress' :
                           rule.action === 'status_review' ? 'Review' :
                           rule.action === 'status_done' ? 'Done' : rule.action}
                        </span>
                      </div>
                      <Switch
                        checked={rule.enabled}
                        onCheckedChange={(enabled) => handleAutoStatusRuleToggle(rule.trigger, enabled)}
                        disabled={saving === 'automation' || !canEdit}
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <div className={cn("pt-4 border-t", colors.borderPrimary)}>
                <Label className={cn("text-sm font-medium mb-3 block", colors.textPrimary)}>
                  Notifications
                </Label>
                <div className={cn("p-4 rounded-lg text-center", colors.bgTertiary)}>
                  <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className={cn("text-sm", colors.textMuted)}>
                    Slack, Discord, and webhook notifications coming soon
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
      )}
    </div>

    {/* ADO Status Mapping Modal */}
    <AdoStatusMappingModal
      open={showMappingModal}
      onClose={setShowMappingModal}
      currentMapping={integrations?.ticketing?.status_mapping}
      onSave={handleStatusMappingSave}
      colors={colors}
      saving={saving === 'ticketing'}
    />
    </>
  );
};

export default ProductIntegrationsSection;
